import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	Container,
	Grid,
	Typography,
	Button,
	Card,
	CardContent,
	Box,
	Slider,
	Checkbox,
	FormGroup,
	FormControlLabel,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	styled,
	Select,
	MenuItem,
	ToggleButton,
	ToggleButtonGroup,
	Chip,
	Divider,
	FormControl,
	InputLabel,
} from '@mui/material'
import {
	EventSeat as EventSeatIcon,
	Schedule as ScheduleIcon,
	DirectionsBus as LocalBus,
	AttachMoney as AttachMoneyIcon,
	ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import moment from 'moment'
import { motion } from 'framer-motion'
import { BusFront, CalendarDays, MenuIcon, SearchX } from 'lucide-react'
import { CustomTabs, Tab, TabPanel } from './CustomTabs'
import { Sheet } from './SlidingModal'
import BookingInterface from './SeatSelection'
import SearchBooking from './SearchForm'
import SeatSelection from './SeatSelection'
import { api } from '../../config/api'

const saccoLogoMap = {
	'a8d7fd86-e7b2-43eb-8d19-843a67ab552a': '/images/MolineLuxuryLogo.png', // Moline Luxury Shuttle
	'72a1e4e1-cd20-4663-858a-efccc1aeee0a': '/images/ExpressShuttle-Primary.png', // Express Shuttle
	'5d1f7fd1-2179-45aa-a024-aeaac76f36c7': '/images/BometTravellers.png', // Bomet Travellers
	'016a4a62ab5b6e49df': '/images/transline.jpg', // Transline
	'7710344a-7696-440e-b21e-75dc01ab9822': '/images/Cherangani.png', // Cherangani Travellers Sacco
	'2e8698ed-d9fe-4892-9646-733fa1bcaa6e': '/images/narokLine.png', // Narok Line
}

const ProviderLogo = styled(motion.img)(({ theme }) => ({
	height: '36px', // Default height
	cursor: 'pointer',
	[theme.breakpoints.down('sm')]: {
		height: '132px', // Smaller height for mobile screens
	},
	[theme.breakpoints.down('sm')]: {
		height: '132px', // Smaller height for mobile screens
	},
	[theme.breakpoints.up('md')]: {
		height: '132px', // Larger height for medium screens and above
	},
	[theme.breakpoints.up('lg')]: {
		height: '132px', // Even larger for large screens
	},
}))

const ContentWrapper = styled(Box)(({ theme }) => ({
	paddingLeft: theme.spacing(2),
	paddingTop: theme.spacing(2),
	height: '100vh',
	display: 'flex',
	flexDirection: 'column',
}))

const FilterSection = styled(Box)(({ theme }) => ({
	width: '100%',
	minWidth: '300px',
	borderRight: '1px solid rgba(0,0,0,0.2)',
	// paddingRight: '16px',
	marginBottom: theme.spacing(2),
}))

const TripsContainer = styled(Box)(({ theme }) => ({
	flexGrow: 1,
	overflowY: 'scroll',
	paddingRight: theme.spacing(2),
	marginTop: theme.spacing(2),
	maxHeight: '70vh',
}))

const TripCard = styled(Card)(({ theme }) => ({
	marginBottom: theme.spacing(2),
	borderRadius: theme.spacing(2),
	transition: 'transform 0.3s ease',
	'&:hover': {
		transform: 'scale(1.02)',
	},
}))

const HeaderControls = styled(Box)(({ theme }) => ({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginBottom: theme.spacing(2),
	borderBottom: `1px solid ${theme.palette.divider}`,
	paddingBottom: theme.spacing(2),
}))

const TabsContainer = styled(Box)(({ theme }) => ({
	flex: 1,
}))

const RouteTrips = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const { pathname } = useLocation()

	const route = location.state?.route
	const { from, to, selectedDate, passengers, transportType } =
		location.state || {}
	const { trip, progressSeats } = location.state || {}

	const [tabValue, setTabValue] = React.useState(0)
	const [openBookingModal, setOpenBookingModal] = React.useState(false)
	const [selectedTrip, setSelectedTrip] = useState([])

	// States for filters and sorting
	const [priceRange, setPriceRange] = useState([0, 5000])
	const [departureTime, setDepartureTime] = useState([0, 24])
	const [ratingFilter, setRatingFilter] = useState('')
	const [selectedSaccos, setSelectedSaccos] = useState([])
	const [activeTab, setActiveTab] = useState(1)
	const [sortBy, setSortBy] = useState('departure_time')
	const [filteredTrips, setFilteredTrips] = useState([])

	const [selectedSeats, setSelectedSeats] = useState([])
	const [sortDirection, setSortDirection] = useState('asc')
	const [saccosList, setSaccosList] = useState([])
	const [searchParams, setSearchParams] = useState({
		departureDate: selectedDate,
		from,
		to,
		transportType,
		passengers,
	})

	// Sorting options
	const sortOptions = [
		{ value: 'departure_time', label: 'Departure Time' },
		{ value: 'price_low', label: 'Price: Low to High' },
		{ value: 'price_high', label: 'Price: High to Low' },
		{ value: 'rating', label: 'Rating' },
		{ value: 'seats_available', label: 'Available Seats' },
	]

	// Tab handling
	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue)
		filterTrips(newValue)
	}

	const handleCloseBookingModal = () => {
		setOpenBookingModal(false)
	}

	// Get date for specific tab
	const getDateForTab = tabIndex => {
		switch (tabIndex) {
			case 0:
				return moment().subtract(1, 'days').startOf('day')
			case 1:
				return moment().startOf('day')
			case 2:
				return moment().add(1, 'days').startOf('day')
			case 3:
				return moment().add(2, 'days').startOf('day')
			default:
				return moment().startOf('day')
		}
	}

	// Filter trips based on selected tab
	const filterTrips = tabIndex => {
		if (!route?.trips) return []

		const targetDate = getDateForTab(tabIndex)

		const filtered = route.trips.filter(trip => {
			const tripDate = moment(trip.departureTime).endOf('day')

			return tripDate.isSame(targetDate, 'day')
		})

		setFilteredTrips(filtered)
	}

	useEffect(() => {
		if (activeTab !== undefined) {
			filterTrips(1)
		}
	}, [activeTab, route?.trips])

	useEffect(() => {
		let isMounted = true

		const fetchInitialTrips = async () => {
			if (!from || !to || !selectedDate) {
				return
			}

			try {
				const parsedDate = new Date(selectedDate)
				const response = await api.get('bookingMobile/trips/searchRange', {
					params: {
						departure: from,
						destination: to,
						departureDate: parsedDate,
					},
				})

				if (isMounted) {
					const fetchedTrips = response.data.data
					const uniqueSaccos = [
						...new Set(fetchedTrips.map(trip => trip.saccoName)),
					]
					setSaccosList(uniqueSaccos)

					// Store the original fetched trips
					setFilteredTrips(fetchedTrips)
				}
			} catch (error) {
				console.error('Error fetching trips:', error)
				if (isMounted) {
					setFilteredTrips([])
				}
			}
		}

		fetchInitialTrips()

		return () => {
			isMounted = false
		}
	}, [from, to, selectedDate])

	useEffect(() => {
		const fetchBookings = async () => {
			const parsedDate = new Date(searchParams.departureDate)

			if (isNaN(parsedDate.getTime())) {
				throw new Error('Invalid date provided for selectedDate')
			}

			try {
				const response = await api.get('bookingMobile/trips/searchRange', {
					params: {
						departure: searchParams.from,
						destination: searchParams.to,
						departureDate: parsedDate,
					},
				})

				let fetchedTrips = response.data.data
				const uniqueSaccos = [
					...new Set(fetchedTrips.map(trip => trip.saccoName)),
				]
				setSaccosList(uniqueSaccos)

				// Apply filters to fetched trips
				const filteredResults = applyFilters(fetchedTrips)
				setFilteredTrips(filteredResults)
			} catch (error) {
				console.error('Error fetching bookings:', error)
				setFilteredTrips([])
			}
		}

		// Only fetch if we have the required search parameters
		if (searchParams.from && searchParams.to && searchParams.departureDate) {
			fetchBookings()
		}
	}, [searchParams])

	const applyFilters = useCallback(
		trips => {
			return trips.filter(trip => {
				const matchesSacco =
					selectedSaccos.length === 0 || selectedSaccos.includes(trip.saccoName)
				const matchesPrice =
					trip.minPrice >= priceRange[0] && trip.minPrice <= priceRange[1]
				const tripTime =
					moment(trip.departureTime).hours() +
					moment(trip.departureTime).minutes() / 60
				const matchesTime =
					tripTime >= departureTime[0] && tripTime <= departureTime[1]
				const matchesRating = !ratingFilter || trip.rating >= ratingFilter

				return matchesSacco && matchesPrice && matchesTime && matchesRating
			})
		},
		[selectedSaccos, priceRange, departureTime, ratingFilter],
	)

	// New useEffect to handle filter changes
	useEffect(() => {
		if (filteredTrips.length > 0) {
			const newFilteredTrips = applyFilters(filteredTrips)
			setFilteredTrips(newFilteredTrips)
		}
	}, [selectedSaccos, priceRange, departureTime, ratingFilter, applyFilters])

	useEffect(() => {
		if (trip && Object.keys(trip).length > 0) {
			setSelectedTrip(trip)
			// setOpenBookingModal(true)
		}
	}, [trip])

	const fetchSeats = async trip => {
		try {
			const url = `/bookingMobile/seats?tripId=${trip._id}`
			const response = await api.get(url)
			if (!response) {
				throw new Error('Failed to fetch seats')
			}

			// Transform API data to match our component's needs
			const transformedSeats = response.data.data
				.map(seat => ({
					id: seat.seatNo,
					category:
						seat.seatType === 'economyClass'
							? 'economy'
							: seat.seatType === 'firstClass'
								? 'first-class'
								: 'premium',
					isReserved: seat.booked || seat.payedFor,
					price: Number(seat.price),
					_id: seat._id,
					...seat,
				}))
				.sort((a, b) => a.id - b.id)
			console.log('transformedSeats', transformedSeats)

			// Find available economy seats
			const availableEconomySeats = transformedSeats.filter(
				seat => !seat.isReserved,
			)
			console.log('availableEconomySeats', availableEconomySeats)
			console.log('passengers', passengers)

			// Determine seats to pre-select
			let selectedSeats = []
			if (searchParams.passengers && searchParams.passengers > 0) {
				selectedSeats = availableEconomySeats
					.slice(0, searchParams.passengers)
					.map(seat => seat._id)
			} else if (availableEconomySeats.length > 0) {
				selectedSeats = [availableEconomySeats[0]._id] // Pick one if no `passengers`
			}
			console.log('selectedSeats', selectedSeats)

			// Update selected seats
			setSelectedSeats(selectedSeats)

			const bookingres = await api.put('bookingMobile/seats/select', {
				data: selectedSeats,
			})
			console.log('bookingres', selectedSeats)

			// Ensure response is valid
			if (bookingres?.data.seats) {
				// Ensure all selected seats are correctly populated
				const populatedSeats = selectedSeats
					.map(seatId => {
						// Check if API response contains this seat ID and it's available
						const isAvailable = bookingres.data.seats.some(
							seatObj => seatObj[seatId],
						)

						// Find the matching seat from transformedSeats
						const matchingSeat = transformedSeats.find(
							seat => seat._id === seatId,
						)

						// Merge data if the seat exists
						return matchingSeat ? { ...matchingSeat, isAvailable } : null
					})
					.filter(Boolean) // Remove null values if no match was found

				// Update state
				setSelectedSeats(populatedSeats)
				console.log('populatedSeats', populatedSeats)

				// Navigate to booking details with populated seats
				navigate('/app/booking/details', {
					state: { trip, selectedSeats: populatedSeats },
				})
			} else {
				console.error('No seats returned from API')
			}
		} catch (error) {
			console.error('Error fetching seats:', error)
		}
	}

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [pathname])

	// Handle sorting
	const handleSortChange = event => {
		setSortBy(event.target.value)
		sortTrips(event.target.value, filteredTrips)
	}

	// Sort trips based on selected option
	const sortTrips = (sortOption, trips) => {
		const sorted = [...trips]
		const newDirection = sortDirection === 'asc' ? 'desc' : 'asc'
		setSortDirection(newDirection)

		const multiplier = newDirection === 'asc' ? 1 : -1

		switch (sortOption) {
			case 'departure_time':
				sorted.sort(
					(a, b) =>
						multiplier * moment(a.departureTime).diff(moment(b.departureTime)),
				)
				break
			case 'price_low':
				sorted.sort((a, b) => multiplier * (a.minPrice - b.minPrice))
				break
			case 'price_high':
				sorted.sort((a, b) => multiplier * (b.minPrice - a.minPrice))
				break
			case 'rating':
				sorted.sort((a, b) => multiplier * (b.rating - a.rating))
				break
			case 'seats_available':
				sorted.sort((a, b) => {
					const availableSeatsA = Number(a.seats) - Number(a.sold)
					const availableSeatsB = Number(b.seats) - Number(b.sold)
					return multiplier * (availableSeatsB - availableSeatsA)
				})
				break
		}

		setFilteredTrips(sorted)
	}

	// Handle rating filter change
	const handleRatingChange = (event, newRating) => {
		setRatingFilter(newRating)
	}

	const handleBook = trip => {
		// navigate(`/trip/${tripId}`)
		setSelectedTrip(trip)
		setOpenBookingModal(true)
	}

	const handleBookNow = trip => {
		setSelectedTrip(trip)
		fetchSeats(trip)
	}

	const formatTimeValue = value => {
		const hours = Math.floor(value)
		const minutes = Math.floor((value - hours) * 60)
		return `${hours.toString().padStart(2, '0')}:${minutes
			.toString()
			.padStart(2, '0')}`
	}

	// Filter handlers
	const handlePriceChange = (event, newValue) => {
		setPriceRange(newValue)
	}

	const handleTimeChange = (event, newValue) => {
		setDepartureTime(newValue)
	}

	const handleSaccoChange = sacco => {
		setSelectedSaccos(prev =>
			prev.includes(sacco)
				? prev.filter(item => item !== sacco)
				: [...prev, sacco],
		)
	}

	// Add handler for SearchBooking changes
	const handleSearchChange = newParams => {
		setSearchParams(prev => ({
			...prev,
			...newParams,
		}))
	}

	const fadeIn = {
		initial: { opacity: 0, y: 20 },
		animate: { opacity: 1, y: 0 },
		transition: { duration: 0.6 },
	}

	const logoVariants = {
		initial: { opacity: 0, x: -20 },
		animate: {
			opacity: 1,
			x: 0,
			transition: {
				duration: 0.6,
			},
		},
	}

	const NoTripsAvailable = () => {
		return (
			<Box
				display="flex"
				flexDirection="column"
				alignItems="center"
				justifyContent="center"
				height="100vh"
				textAlign="center"
				sx={{
					backgroundColor: '#f5f5f5',
					padding: 4,
					gap: 2,
				}}
			>
				{/* Animated Icon */}
				<motion.div
					animate={{ y: [0, -10, 0] }}
					transition={{
						duration: 2,
						repeat: Infinity,
						ease: 'easeInOut',
					}}
				>
					<SearchX
						style={{
							fontSize: 80,
							color: 'primary.main',
						}}
					/>
				</motion.div>

				{/* Header Text */}
				<Typography variant="h4" fontWeight="bold" color="text.primary">
					No Trips Available
				</Typography>

				{/* Subtext */}
				<Typography
					variant="body1"
					color="text.secondary"
					sx={{ maxWidth: 400 }}
				>
					We're sorry! Looks like there are no trips available in this category
					right now. Check back later for more exciting adventures!
				</Typography>

				{/* Call-to-Action Button */}
				<motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
					<Button
						variant="contained"
						color="primary"
						size="large"
						onClick={() => console.log('Redirecting to explore page...')}
					>
						Explore Popular Trips
					</Button>
				</motion.div>
			</Box>
		)
	}
	const getSaccoLogo = appid => {
		return saccoLogoMap[appid] || null
	}

	const tripList = useMemo(() => {
		if (!Array.isArray(filteredTrips) || filteredTrips.length === 0) {
			return (
				<Grid item xs={12} md={12}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							flexDirection: 'column',
							gap: 2,
							padding: 4,
						}}
					>
						<Typography
							variant="h6"
							sx={{
								fontWeight: '500',
								color: 'rgba(0,0,0,0.7)',
								textAlign: 'center',
							}}
						>
							No trips available
						</Typography>
						<Typography
							variant="body2"
							sx={{
								color: 'rgba(0,0,0,0.5)',
								textAlign: 'center',
							}}
						>
							Please adjust your filters and try again later.
						</Typography>
					</Box>
				</Grid>
			)
		}

		return filteredTrips.map((trip, index) => (
			<Grid item xs={12} md={12} key={index}>
				<Card
					// className={classes.tripCard}
					// elevation={3}
					sx={{
						display: 'flex',
						gap: 2,
						height: '214px',
						justifyContent: 'center',
						maxWidth: '840px',
						margin: '24px auto',
						boxShadow: '0px 4px 16px 0px rgba(17, 34, 17, 0.05)',
					}}
				>
					<CardContent
						sx={{
							width: '100%',
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'space-between',
							padding: '16px !important',
							gap: 2,
						}}
					>
						{/* <Box
								sx={{
									display: 'flex',
									marginTop: '8px',
								}}
							>
								<ProviderLogo
									src="/images/modernCoast.svg"
									alt="logo"
									variants={logoVariants}
								/>
							</Box> */}
						<Box
							sx={{
								display: 'flex',
								marginTop: '8px',
							}}
						>
							{getSaccoLogo(trip.appid) ? (
								<ProviderLogo
									src={getSaccoLogo(trip.appid)}
									alt={`${trip.saccoName} logo`}
									variants={logoVariants}
								/>
							) : (
								<Box
									sx={{
										width: '146px',
										height: '132px',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										backgroundColor: '#e0e0e0',
										borderRadius: '8px',
										fontSize: '16px',
										fontWeight: '600',
										color: '#333',
										textAlign: 'center',
									}}
								>
									{trip.saccoName?.toUpperCase()}
								</Box>
							)}
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								justifyContent: 'space-between',
							}}
						>
							<Typography
								variant="h5"
								sx={{ fontWeight: '500', color: 'rgb(96, 97, 100)' }}
							>
								{trip.saccoName}
							</Typography>
							<div
								// className={classes.tripDetails}
								style={{
									display: 'flex',
									flexDirection: 'row',
									gap: '12px',
									alignItems: 'center',
								}}
							>
								<Chip
									icon={
										<BusFront
											style={{
												fontSize: 14,
												verticalAlign: 'middle',
												color: 'f9a84e',
											}}
										/>
									}
									label={`${trip.vehicleno}`}
									sx={{ borderRadius: '6px', height: '28px' }}
								/>
								<Chip
									icon={
										<CalendarDays
											style={{
												fontSize: 13,
												verticalAlign: 'middle',
												color: 'f9a84e',
											}}
										/>
									}
									label={`${moment(trip.departureTime).format('ll')}`}
									sx={{ borderRadius: '6px', height: '28px' }}
								/>
								<Chip
									icon={
										<BusFront
											style={{
												fontSize: 14,
												verticalAlign: 'middle',
												color: 'f9a84e',
											}}
										/>
									}
									label={`${Number(trip.seats - trip.sold)} seats remaining`}
									sx={{ borderRadius: '6px', height: '28px' }}
								/>
								<Chip
									icon={
										<BusFront
											style={{
												fontSize: 14,
												verticalAlign: 'middle',
												color: 'f9a84e',
											}}
										/>
									}
									label={`Standard Class`}
									sx={{ borderRadius: '6px', height: '28px' }}
								/>
							</div>
							<Box
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
								}}
							>
								<Box sx={{ display: 'flex', gap: 2 }}>
									<Box sx={{ display: 'flex', flexDirection: 'column' }}>
										<Box sx={{ display: 'flex' }}>
											<Typography
												variant="h6"
												color="#000"
												sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
											>
												{trip.departure}
											</Typography>
											<Typography
												variant="h6"
												color="rgba(0,0,0,0.2)"
												sx={{ fontWeight: 400, marginLeft: '2px' }}
											>
												-----
											</Typography>
										</Box>
										<Box>
											<Typography
												variant="body2"
												color="grey"
												sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
											>
												{moment(trip.departureTime).format('LT')}
											</Typography>
										</Box>
									</Box>
									<Typography
										variant="h6"
										color="#000"
										sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
									>
										to
									</Typography>
									<Box sx={{ display: 'flex', flexDirection: 'column' }}>
										<Box sx={{ display: 'flex' }}>
											<Typography
												variant="h6"
												color="#000"
												sx={{ fontWeight: 500, color: 'rgb(96, 97, 100)' }}
											>
												{trip.destination}
											</Typography>
											<Typography
												variant="h6"
												color="rgba(0,0,0,0.2)"
												sx={{ fontWeight: 400, marginLeft: '2px' }}
											>
												-----
											</Typography>
										</Box>
										<Box>
											<Typography
												variant="body2"
												color="grey"
												sx={{ fontWeight: 500 }}
											>
												{/* {moment(trip.departureTime)
													.add(4, 'hours')
													.format('LT')} */}
											</Typography>
										</Box>
									</Box>
								</Box>
								<Box style={{ display: 'flex', alignItems: 'end' }}>
									<Typography
										sx={{ fontWeight: '500' }}
										variant="h6"
										color="#ef404f"
									>
										<span>
											{new Intl.NumberFormat('en-KE', {
												style: 'currency',
												currency: 'KES',
											}).format(trip.minPrice)}
										</span>
									</Typography>
								</Box>
							</Box>
							<Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
								<Box
									sx={{
										display: 'flex',
										gap: 1,
										alignItems: 'center', // Align items to the bottom
									}}
								>
									<Box
										sx={{
											width: '40px',
											height: '32px',
											backgroundColor: '#fff',
											flex: '0 0 28px',
											fontSize: '10px',
											fontWeight: '600',
											color: '#000',
											border: '1px solid green',
											borderRadius: '6px',
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
											padding: '10px',
										}}
									>
										4.2
									</Box>
									<Typography
										sx={{
											fontWeight: '600',
											textAlign: 'end',
											marginBottom: '0',
											lineHeight: 1, // Ensures consistent vertical alignment
										}}
										variant="body1"
									>
										Very Good
									</Typography>
									<Typography
										sx={{
											fontWeight: '600',
											color: 'grey',
											marginBottom: '0',
											lineHeight: 1, // Consistent alignment
										}}
										variant="body2"
									>
										54 reviews
									</Typography>
								</Box>

								<Box sx={{ display: 'flex', gap: 2 }}>
									<Button
										onClick={() => handleBook(trip)}
										variant="contained"
										color="primary"
										sx={{ width: '120px', height: '32px' }}
									>
										Select Seat
									</Button>
									<Button
										onClick={() => handleBookNow(trip)}
										variant="contained"
										color="primary"
										sx={{ width: '120px', height: '32px' }}
									>
										Book Now
									</Button>
								</Box>
							</Box>
						</Box>
					</CardContent>
					{/* <CardActions> */}

					{/* </CardActions> */}
				</Card>
				<Sheet
					open={openBookingModal}
					onClose={handleCloseBookingModal}
					title="Select Seats"
					description="Select a suitable seat from the available seats below."
				>
					<SeatSelection
						trip={selectedTrip || trip}
						progressSeats={progressSeats}
					/>
				</Sheet>
			</Grid>
		))
	}, [filteredTrips, handleBook])

	const yesterday = {
		date: moment().subtract(1, 'days').format('YYYY-MM-DD'),
		day: moment().subtract(1, 'days').format('dddd'),
	}
	const today = {
		date: moment().format('YYYY-MM-DD'),
		day: moment().format('dddd'),
	}
	const tomorrow = {
		date: moment().add(1, 'days').format('YYYY-MM-DD'),
		day: moment().add(1, 'days').format('dddd'),
	}
	const dayAfterTomorrow = {
		date: moment().add(2, 'days').format('YYYY-MM-DD'),
		day: moment().add(2, 'days').format('dddd'),
	}

	return (
		<>
			<Container maxWidth="xl">
				<Box sx={{}}>
					<SearchBooking
						from={searchParams.from}
						to={searchParams.to}
						selectedDate={searchParams.departureDate}
						passengers={searchParams.passengers}
						transportType={searchParams.transportType}
						onSearch={handleSearchChange}
					/>
				</Box>
				<Box
					sx={{
						margin: '0',
						padding: '0',
						display: 'flex',
						flexDirection: { xs: 'column', sm: 'row' },
					}}
				>
					{/* Left Column - Filters */}
					<Box
					// item xs={12} md={4} lg={4} xl={4}
					>
						<FilterSection>
							{/* Price Filter */}
							<Accordion defaultExpanded>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography variant="subtitle1">Price Range (KES)</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Slider
										value={priceRange}
										onChange={handlePriceChange}
										valueLabelDisplay="auto"
										min={0}
										max={5000}
										step={100}
									/>
									<Box display="flex" justifyContent="space-between">
										<Typography>KES {priceRange[0]}</Typography>
										<Typography>KES {priceRange[1]}</Typography>
									</Box>
								</AccordionDetails>
							</Accordion>
							<hr
								style={{
									border: 'none',
									height: '1px',
									backgroundColor: 'rgba(0,0,0,0.2)',
								}}
							/>

							{/* Departure Time Filter */}
							<Accordion defaultExpanded>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography variant="subtitle1">Departure Time</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Slider
										value={departureTime}
										onChange={handleTimeChange}
										valueLabelDisplay="auto"
										valueLabelFormat={formatTimeValue}
										min={0}
										max={24}
										step={0.5}
									/>
									<Box display="flex" justifyContent="space-between">
										<Typography>{formatTimeValue(departureTime[0])}</Typography>
										<Typography>{formatTimeValue(departureTime[1])}</Typography>
									</Box>
								</AccordionDetails>
							</Accordion>
							<hr
								style={{
									border: 'none',
									height: '1px',
									backgroundColor: 'rgba(0,0,0,0.2)',
								}}
							/>
							{/* Rating Filter */}
							<Accordion defaultExpanded>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography variant="subtitle1">Minimum Rating</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<ToggleButtonGroup
										value={ratingFilter}
										exclusive
										onChange={handleRatingChange}
										sx={{
											gap: '8px', // Add spacing between buttons
											'& .MuiToggleButton-root': {
												// Target all ToggleButtons
												margin: 0, // Remove individual margins
											},
											'& .MuiToggleButtonGroup-grouped': {
												// Override MUI's default grouped styles
												borderRadius: '6px !important', // Force border radius
												border: '1px solid #ef404f !important', // Force border
												marginLeft: '0 !important', // Remove MUI's default margins
												marginRight: '0 !important',
												'&:not(:first-of-type)': {
													borderLeft: '1px solid #ef404f !important', // Ensure each button has its own border
												},
											},
										}}
									>
										{[1, 2, 3, 4, 5].map(rating => (
											<ToggleButton
												key={rating}
												value={rating}
												sx={{
													width: '28px',
													height: '28px',
													backgroundColor: '#fff',
													flex: '0 0 28px', // Changed to flex-shrink: 0 and explicit flex-basis
													fontSize: '10px',
													color: '#000',
													'&.Mui-selected': {
														backgroundColor: '#ef404f',
														color: '#fff',
														'&:hover': {
															backgroundColor: '#ef404f',
														},
													},
												}}
											>
												{rating}+
											</ToggleButton>
										))}
									</ToggleButtonGroup>
								</AccordionDetails>
							</Accordion>
							<hr
								style={{
									border: 'none',
									height: '1px',
									backgroundColor: 'rgba(0,0,0,0.2)',
								}}
							/>
							{/* Sacco Filter */}
							<Accordion defaultExpanded>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography variant="subtitle1">
										Transport Companies
									</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<FormGroup>
										{saccosList.map(sacco => (
											<FormControlLabel
												key={sacco}
												control={
													<Checkbox
														checked={selectedSaccos.includes(sacco)}
														onChange={() => handleSaccoChange(sacco)}
														color="secondary"
													/>
												}
												label={sacco}
											/>
										))}
									</FormGroup>
								</AccordionDetails>
							</Accordion>
						</FilterSection>
					</Box>

					<Box
						// item
						// xs={12}
						// md={8}
						// lg={8}
						// xl={8}
						sx={{
							height: '100%',
							display: 'flex',
							flexDirection: 'column',
							width: '100%',
						}}
					>
						<ContentWrapper>
							<HeaderControls>
								<TabsContainer>
									<CustomTabs value={activeTab} onChange={setActiveTab}>
										<Tab
											value={0}
											header={yesterday.date}
											caption={yesterday.day}
											onChange={setActiveTab}
											selectedValue={activeTab}
										/>
										<Divider
											orientation="vertical"
											flexItem
											sx={{ margin: '16px 0' }}
										/>
										<Tab
											value={1}
											header={today.date}
											caption={today.day}
											onChange={setActiveTab}
											selectedValue={activeTab}
										/>
										<Divider
											orientation="vertical"
											flexItem
											sx={{ margin: '16px 0' }}
										/>

										<Tab
											value={2}
											header={tomorrow.date}
											caption={tomorrow.day}
											onChange={setActiveTab}
											selectedValue={activeTab}
										/>
										<Divider
											orientation="vertical"
											flexItem
											sx={{ margin: '16px 0' }}
										/>

										<Tab
											value={4}
											header={dayAfterTomorrow.date}
											caption={dayAfterTomorrow.day}
											onChange={setActiveTab}
											selectedValue={activeTab}
										/>
									</CustomTabs>
									<Box
										sx={{
											mt: 2,
											display: 'flex',
											justifyContent: 'flex-end',
											alignItems: 'center',
										}}
									>
										<InputLabel
											sx={{ fontSize: '14px', fontWeight: '600', mr: 1 }}
										>
											Sort By
										</InputLabel>
										<FormControl variant="standard">
											<Select
												value={sortBy}
												onChange={handleSortChange}
												size="small"
												displayEmpty
												sx={{ fontSize: '14px', fontWeight: '600' }}
											>
												{sortOptions.map(option => (
													<MenuItem
														sx={{ fontSize: '12px' }}
														key={option.value}
														value={option.value}
													>
														{option.label}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Box>
									<TabPanel selectedValue={activeTab} value={0}>
										{/* Scrollable Trips List */}
										<TripsContainer>{tripList}</TripsContainer>
									</TabPanel>
									<TabPanel selectedValue={activeTab} value={1}>
										{/* Scrollable Trips List */}
										<TripsContainer>{tripList}</TripsContainer>
									</TabPanel>
									<TabPanel selectedValue={activeTab} value={2}>
										{/* Scrollable Trips List */}
										<TripsContainer>{tripList}</TripsContainer>
									</TabPanel>
									<TabPanel selectedValue={activeTab} value={4}>
										{/* Scrollable Trips List */}
										<TripsContainer>{tripList}</TripsContainer>
									</TabPanel>
								</TabsContainer>
							</HeaderControls>
						</ContentWrapper>
					</Box>
				</Box>
			</Container>
		</>
	)
}

export default RouteTrips
